import { ApiError, ERROR_CODES } from './errors';

export type EpochMillis = number;

export const MIN_EPOCH_MILLIS: EpochMillis = 0;

export const MAX_EPOCH_MILLIS: EpochMillis = 253402300799999;

export const MILLIS_PER_SECOND = 1000;
export const MILLIS_PER_MINUTE = 60 * MILLIS_PER_SECOND;
export const MILLIS_PER_HOUR = 60 * MILLIS_PER_MINUTE;
export const MILLIS_PER_DAY = 24 * MILLIS_PER_HOUR;

export function nowMillis(): EpochMillis {
  return Date.now();
}

export function isEpochMillis(value: unknown): value is EpochMillis {
  return (
    typeof value === 'number' &&
    Number.isSafeInteger(value) &&
    value >= MIN_EPOCH_MILLIS &&
    value <= MAX_EPOCH_MILLIS
  );
}

export function requireEpochMillis(value: unknown, field: string): EpochMillis {
  if (!isEpochMillis(value)) {
    throw new ApiError(ERROR_CODES.VALIDATION_FAILED, {
      details: { fields: [{ path: field, rule: 'epoch_millis' }] },
    });
  }
  return value;
}

export function toEpochMillis(value: Date | EpochMillis): EpochMillis {
  return value instanceof Date
    ? requireEpochMillis(value.getTime(), 'date')
    : requireEpochMillis(value, 'timestamp');
}

export function toDate(value: EpochMillis): Date {
  return new Date(requireEpochMillis(value, 'timestamp'));
}

export function toIsoString(value: EpochMillis): string {
  return toDate(value).toISOString();
}

export function fromIsoString(value: string, field = 'timestamp'): EpochMillis {
  const parsed = Date.parse(value);
  if (!isEpochMillis(parsed)) {
    throw new ApiError(ERROR_CODES.VALIDATION_FAILED, {
      details: { fields: [{ path: field, rule: 'iso_timestamp' }] },
    });
  }
  return parsed;
}

export function addMillis(base: EpochMillis, delta: number): EpochMillis {
  return requireEpochMillis(base + delta, 'timestamp');
}

export function secondsLater(from: EpochMillis, seconds: number): EpochMillis {
  return addMillis(from, Math.trunc(seconds) * MILLIS_PER_SECOND);
}

export function minutesLater(from: EpochMillis, minutes: number): EpochMillis {
  return addMillis(from, Math.trunc(minutes) * MILLIS_PER_MINUTE);
}

export function daysLater(from: EpochMillis, days: number): EpochMillis {
  return addMillis(from, Math.trunc(days) * MILLIS_PER_DAY);
}

export function hasPassed(value: EpochMillis, reference: EpochMillis = nowMillis()): boolean {
  requireEpochMillis(value, 'timestamp');
  requireEpochMillis(reference, 'reference');
  return value <= reference;
}

export function remainingMillis(value: EpochMillis, reference: EpochMillis = nowMillis()): number {
  requireEpochMillis(value, 'timestamp');
  return Math.max(0, value - reference);
}

export function compareEpochMillis(left: EpochMillis, right: EpochMillis): number {
  return left === right ? 0 : left < right ? -1 : 1;
}

export function isValidWindow(start: EpochMillis, end: EpochMillis): boolean {
  return isEpochMillis(start) && isEpochMillis(end) && start <= end;
}

export function assertWindow(
  start: unknown,
  end: unknown,
  startField: string,
  endField: string,
): void {
  const startMillis = requireEpochMillis(start, startField);
  const endMillis = requireEpochMillis(end, endField);
  if (!isValidWindow(startMillis, endMillis)) {
    throw new ApiError(ERROR_CODES.VALIDATION_FAILED, {
      details: { fields: [{ path: endField, rule: 'after_start', related: startField }] },
    });
  }
}

export function truncateToSecond(value: EpochMillis): EpochMillis {
  return Math.floor(requireEpochMillis(value, 'timestamp') / MILLIS_PER_SECOND) * MILLIS_PER_SECOND;
}

export function dateStamp(value: EpochMillis): string {
  return toIsoString(value).slice(0, 10);
}
